import { useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled, { css } from 'styled-components';

import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttachment } from '@components/icons/attachment';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Tooltip } from '@components/tooltip';

import { Modal } from 'features/modals';
import { JobFormQuestionTypes, JobFormQuestionEdit } from 'models/jobs';
import { useStore } from 'models/Provider';

const itemsDisplay = {
  one_line: { icon: <IconTextShort />, text: 'short' },
  multi_line: { icon: <IconTextLong />, text: 'para' },
  dropdown: { icon: <IconSelectSingle />, text: 'single' },
  multidropdown: { icon: <IconSelectMultiple />, text: 'multi' },
  attachment: { icon: <IconAttachment />, text: 'attachment' },
};

function QuestionModal({ questionId, job, closeModal }) {
  const { t } = useStore();
  const model = useState(() => JobFormQuestionEdit.create({ job, questionId }))[0];
  const question = model.current;
  const optionPlaceholders = model.optionPlaceholders;
  const titlePlaceholder = model.titlePlaceholder;

  const questionTypes = JobFormQuestionTypes.map((key) => ({
    value: key,
    label: (
      <Stack space={2} orientation="center" align="center" key={key}>
        {itemsDisplay[key].icon}
        <div>{t(`job.create.form.questions.type.${itemsDisplay[key].text}`)}</div>
      </Stack>
    ),
  }));

  function submit(e) {
    if (e) {
      e.preventDefault();
    }
    if (model.submitForJob(job)) {
      closeModal();
    }
  }
  return (
    <Modal>
      <Modal.Header
        text={
          questionId
            ? t('job.create.form.questions.title.edit')
            : t('job.create.form.questions.title')
        }
      />
      <LanguageSwitcher model={model} t={t} />

      <Stack space={4}>
        <Select
          label={t('job.create.form.questions.type.label')}
          onChange={model.setType}
          defaultValue={question.type}
          options={questionTypes}
          isSearchable={false}
          isClearable={false}
        />

        <TextField
          label={t('job.create.form.questions.qlabel')}
          name="title"
          placeholder={titlePlaceholder}
          onChange={question.setTitle}
          value={question.title}
          error={question.error && t('job.create.form.questions.qerror')}
        />
        {question.type === 'attachment' && (
          <div>
            <FormLabel text={t('job.create.form.questions.multipleattachments.label')} />
            <Stack space={0} orientation="horizontal" justify="space-between">
              <Text
                text={
                  question.multiple
                    ? t('job.create.form.questions.multipleattachments.yes')
                    : t('job.create.form.questions.multipleattachments.no')
                }
              />
              <Switch
                onChange={model.toggleMultiple}
                defaultValue={question.multiple}
                label="Multiple files"
                labelVisible={false}
              />
            </Stack>
          </div>
        )}
        {(question.type === 'multidropdown' || question.type === 'dropdown') && (
          <div>
            <FormLabel text={t('job.create.form.questions.qopts')} />
            <Stack space={2}>
              {question.options.map((o, i) => (
                <Stack space={2} orientation="horizontal" key={i}>
                  <div style={{ width: '100%' }}>
                    <TextField
                      hideLabel
                      label={t('job.create.form.questions.qopts')}
                      placeholder={optionPlaceholders.length > 0 ? optionPlaceholders[i] : ''}
                      fullSize
                      name={`option${i}`}
                      onChange={(val) => question.editOption(i, val)}
                      value={question.options[i].display}
                      error={question.options[i].error && t('job.create.form.options.empty')}
                    />
                  </div>
                  <IconButton
                    onClick={() => {
                      if (question.options.length > 1) {
                        // Dont allow the user to clear all options, makes no sense.
                        model.removeOption(i);
                      }
                    }}
                    tooltip={t('job.create.form.options.tip.remove')}
                    icon={<IconCloseRegular />}
                  />
                  <IconButton
                    onClick={() => model.addOption(i)}
                    tooltip={t('job.create.form.options.tip.add')}
                    icon={<IconAddPlus />}
                  />
                </Stack>
              ))}
            </Stack>
          </div>
        )}
      </Stack>
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          text={t('job.create.form.questions.button.cancel')}
        />
        <Button
          onClick={submit}
          text={t(`job.create.form.questions.button.submit${questionId ? 'edit' : ''}`)}
        />
      </Modal.Actions>
      {model.langErrors.find(Boolean) && (
        <Stack space={0} orientation="horizontal" justify="flex-end">
          <FormError text={t('job.create.form.questions.error')} />
        </Stack>
      )}
    </Modal>
  );
}

const LanguageSwitcher = observer(({ model, t }) => {
  if (model.langs.length < 2) return null;

  const errors = model.langErrors;

  return (
    <div style={{ display: 'flex', marginBottom: 30 }}>
      {model.langs.map((lang, index) => (
        <Tooltip key={lang} content={errors[index] ? 'Language data contains errors' : ''}>
          <LanguageTab
            active={model.selectedLanguage === lang}
            onClick={() => model.selectLanguage(lang)}
            error={errors[index]}
          >
            {t(`general.${lang}`)}
          </LanguageTab>
        </Tooltip>
      ))}
    </div>
  );
});

const LanguageTab = styled.div`
  font-size: 13px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  ${(p) => p.active && 'font-weight: bold'};
  color: ${(p) => (p.active ? p.theme.colors.blue100 : p.theme.textColor)};
  opacity: ${(p) => (p.active ? 1 : 0.8)};
  margin-right: 16px;
  :hover {
    cursor: ${(p) => (p.active ? 'initial' : 'pointer')};
  }

  ${(p) =>
    p.error &&
    !p.active &&
    css`
      font-weight: bold;
      color: ${(p) => p.theme.errorColor};
    `};
`;

export default observer(QuestionModal);
